import React, { useState } from 'react';
import { Eye, Check, X, Trash2, User, Mail, Phone, Building, Briefcase, FileText, Calendar, Send } from 'lucide-react';
import toast from 'react-hot-toast';
import { useSupabase } from '../../hooks/useSupabase';
import { aicisAPI } from '../../services/api';
import DataTable from '../../components/Tables/DataTable';
import Modal from '../../components/UI/Modal';
import { formatDate, getStatusColor, getStatusLabel } from '../../utils/helpers';
import { sendEmail } from '../../services/emailService';

export default function Speakers() {
  const { data, refetch } = useSupabase(aicisAPI.getSpeakers);
  const [processingId, setProcessingId] = useState(null);
  const [selectedSpeaker, setSelectedSpeaker] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  const handleStatusUpdate = async (id, status) => {
    setProcessingId(id);
    try {
      await aicisAPI.updateSpeakerStatus(id, status);
      toast.success(`Speaker ${status} successfully`);
      refetch();
      setIsModalOpen(false);
      setSelectedSpeaker(null);
      setShowEmailForm(false);
    } catch (error) {
      toast.error(`Failed to ${status} speaker`);
    } finally {
      setProcessingId(null);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this speaker?')) {
      setProcessingId(id);
      try {
        await aicisAPI.deleteSpeaker(id);
        toast.success('Speaker deleted successfully');
        refetch();
        setIsModalOpen(false);
        setSelectedSpeaker(null);
        setShowEmailForm(false);
      } catch (error) {
        toast.error('Failed to delete speaker');
      } finally {
        setProcessingId(null);
      }
    }
  };

  const handleView = (item) => {
    setSelectedSpeaker(item);
    setIsModalOpen(true);
    setShowEmailForm(false);
    setEmailSubject(`Regarding Your Speaker Application - ${item.full_name}`);
    setEmailMessage(`Dear ${item.full_name || 'Sir/Madam'},

Thank you for your interest in speaking at our conference. We have reviewed your application and would like to discuss further.

Best regards,
AICIS Conference Team`);
  };

  const handleSendEmail = async () => {
    if (!selectedSpeaker) return;
    
    setIsSendingEmail(true);
    try {
      await sendEmail({
        to: selectedSpeaker.email,
        subject: emailSubject,
        message: emailMessage,
        fromName: process.env.REACT_APP_SENDER_NAME || 'AICIS Conference Team',
      });
      
      toast.success(`✅ Email sent to ${selectedSpeaker.email}`);
      setShowEmailForm(false);
      setEmailSubject('');
      setEmailMessage('');
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Failed to send email. Please check your configuration.');
    } finally {
      setIsSendingEmail(false);
    }
  };

  const renderSpeakerDetails = (speaker) => {
    if (!speaker) return null;

    if (showEmailForm) {
      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-semibold text-gray-700">Compose Email</h4>
            <button
              onClick={() => setShowEmailForm(false)}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Back to Details
            </button>
          </div>
          
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-xs text-gray-600">From:</p>
            <p className="text-sm font-medium text-gray-900">{process.env.REACT_APP_SENDER_NAME || 'AICIS Conference Team'} &lt;{process.env.REACT_APP_SENDER_EMAIL || 'conference@aicis.com'}&gt;</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To:</label>
            <div className="p-2 bg-gray-50 rounded-lg border border-gray-200">
              <span className="text-sm text-gray-900">{speaker.email}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject:</label>
            <input
              type="text"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Enter email subject"
              disabled={isSendingEmail}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Message:</label>
            <textarea
              value={emailMessage}
              onChange={(e) => setEmailMessage(e.target.value)}
              rows="8"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              placeholder="Type your message here..."
              disabled={isSendingEmail}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              onClick={handleSendEmail}
              disabled={isSendingEmail || !emailSubject || !emailMessage}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="w-4 h-4" />
              {isSendingEmail ? 'Sending...' : 'Send Email'}
            </button>
            <button
              onClick={() => setShowEmailForm(false)}
              className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isSendingEmail}
            >
              Cancel
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Profile Image */}
        {speaker.photo_url && (
          <div className="flex justify-center">
            <div className="relative w-32 h-32 rounded-full overflow-hidden border-4 border-gray-200">
              <img 
                src={speaker.photo_url} 
                alt={speaker.full_name} 
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://via.placeholder.com/128x128/CCCCCC/FFFFFF?text=No+Image';
                }}
              />
            </div>
          </div>
        )}

        {/* Basic Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
            <User className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <p className="text-xs text-gray-500">Full Name</p>
              <p className="text-sm font-medium text-gray-900">{speaker.full_name || 'N/A'}</p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
            <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <p className="text-xs text-gray-500">Email</p>
              <p className="text-sm font-medium text-gray-900">{speaker.email || 'N/A'}</p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
            <Phone className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <p className="text-xs text-gray-500">Phone</p>
              <p className="text-sm font-medium text-gray-900">{speaker.phone || 'N/A'}</p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
            <Building className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <p className="text-xs text-gray-500">Organization</p>
              <p className="text-sm font-medium text-gray-900">{speaker.organization || 'N/A'}</p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
            <Briefcase className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <p className="text-xs text-gray-500">Position</p>
              <p className="text-sm font-medium text-gray-900">{speaker.position || 'N/A'}</p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
            <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <p className="text-xs text-gray-500">Topic</p>
              <p className="text-sm font-medium text-gray-900">{speaker.topic || 'N/A'}</p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
            <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <p className="text-xs text-gray-500">Applied On</p>
              <p className="text-sm font-medium text-gray-900">{formatDate(speaker.created_at)}</p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
            <div className="w-5 h-5 flex items-center justify-center">
              <span className="text-xs font-bold text-gray-400">#</span>
            </div>
            <div>
              <p className="text-xs text-gray-500">Status</p>
              <span className={`badge ${getStatusColor(speaker.status)} mt-1`}>
                {getStatusLabel(speaker.status)}
              </span>
            </div>
          </div>
        </div>

        {/* Bio Section */}
        {speaker.bio && (
          <div className="p-4 bg-gray-50 rounded-lg">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Bio</h4>
            <p className="text-sm text-gray-600 whitespace-pre-wrap">{speaker.bio}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col gap-3 pt-4 border-t border-gray-200">
          {/* Email Response Button */}
          <button
            onClick={() => setShowEmailForm(true)}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            <Send className="w-4 h-4" />
            Respond with Email
          </button>

          {/* Status Action Buttons */}
          {speaker.status === 'pending' && (
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => handleStatusUpdate(speaker.id, 'approved')}
                disabled={processingId === speaker.id}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Check className="w-4 h-4" />
                {processingId === speaker.id ? 'Processing...' : 'Approve Speaker'}
              </button>
              <button
                onClick={() => handleStatusUpdate(speaker.id, 'rejected')}
                disabled={processingId === speaker.id}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <X className="w-4 h-4" />
                {processingId === speaker.id ? 'Processing...' : 'Reject Speaker'}
              </button>
            </div>
          )}

          {speaker.status !== 'pending' && (
            <div className="pt-2">
              <p className="text-sm text-gray-500 text-center">
                This speaker has been <span className={`font-semibold ${speaker.status === 'approved' ? 'text-green-600' : 'text-red-600'}`}>
                  {getStatusLabel(speaker.status)}
                </span>
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const columns = [
    { key: 'full_name', label: 'Full Name', sortable: true },
    { key: 'email', label: 'Email', sortable: true },
    { key: 'phone', label: 'Phone' },
    { key: 'organization', label: 'Organization' },
    { key: 'position', label: 'Position' },
    { key: 'topic', label: 'Topic' },
    { 
      key: 'status', 
      label: 'Status',
      render: (value) => (
        <span className={`badge ${getStatusColor(value)}`}>
          {getStatusLabel(value)}
        </span>
      )
    },
    { 
      key: 'created_at', 
      label: 'Applied',
      render: (value) => formatDate(value)
    },
  ];

  const actions = [
    {
      label: 'View',
      icon: <Eye className="w-4 h-4" />,
      onClick: (item) => handleView(item),
      className: 'text-blue-600 hover:bg-blue-50',
      showWhen: ['pending', 'approved', 'rejected']
    },
    {
      label: 'Approve',
      icon: <Check className="w-4 h-4" />,
      onClick: (item) => handleStatusUpdate(item.id, 'approved'),
      className: 'text-green-600 hover:bg-green-50',
      showWhen: ['pending'],
      disabled: (item) => processingId === item.id
    },
    {
      label: 'Reject',
      icon: <X className="w-4 h-4" />,
      onClick: (item) => handleStatusUpdate(item.id, 'rejected'),
      className: 'text-red-600 hover:bg-red-50',
      showWhen: ['pending'],
      disabled: (item) => processingId === item.id
    },
    {
      label: 'Delete',
      icon: <Trash2 className="w-4 h-4" />,
      onClick: (item) => handleDelete(item.id),
      className: 'text-gray-600 hover:bg-gray-50',
      showWhen: ['pending', 'approved', 'rejected'],
      disabled: (item) => processingId === item.id
    },
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Speakers</h1>
        <p className="text-gray-600">Manage conference speakers</p>
      </div>

      <DataTable
        columns={columns}
        data={data || []}
        title={`Total: ${data?.length || 0} speakers`}
        actions={actions}
        searchPlaceholder="Search speakers..."
        onRowClick={(item) => handleView(item)}
      />

      {/* Speaker Details Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedSpeaker(null);
          setShowEmailForm(false);
        }}
        title={showEmailForm ? "Compose Email" : "Speaker Details"}
        size="max-w-3xl"
      >
        {renderSpeakerDetails(selectedSpeaker)}
      </Modal>
    </div>
  );
}